import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Slide,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  LocalHospital as HospitalIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BrokenImage as BrokenImageIcon,
  Biotech as BiotechIcon,
  Visibility as VisibilityIcon,
  RemoveRedEye as EyeIcon,
  Healing as HealingIcon
} from '@mui/icons-material';

const API_URL = 'https://turismo.nara.chat/api';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TIPOS_ESTUDIO = [
  { value: 'radiografia', label: 'Radiografía', desc: 'Rayos X de tórax, extremidades, etc.', icon: <BrokenImageIcon /> },
  { value: 'dermatologia', label: 'Dermatología', desc: 'Lesiones cutáneas, manchas, lunares', icon: <HealingIcon /> },
  { value: 'oftalmologia', label: 'Oftalmología', desc: 'Fondo de ojo, córnea', icon: <EyeIcon /> },
  { value: 'ecografia', label: 'Ecografía', desc: 'Imágenes de ultrasonido', icon: <BiotechIcon /> },
  { value: 'tomografia', label: 'Tomografía', desc: 'Cortes de TC', icon: <BiotechIcon /> },
  { value: 'resonancia', label: 'Resonancia', desc: 'Imágenes de RM', icon: <BiotechIcon /> },
  { value: 'otro', label: 'Otro', desc: 'Otros tipos de imágenes médicas', icon: <ImageIcon /> }
];

// Componente de sección con estilo consistente
const SeccionFormulario = ({ icon, titulo, color = "#6366f1", children }) => (
  <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
    <Typography sx={{ fontWeight: 600, color: "#1e3a5f", fontSize: 14, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
      {titulo}
    </Typography>
    {children}
  </Box>
);

const AnalizadorImagenes = ({
  open,
  onClose,
  estudioId,
  expedienteId,
  paciente,
  userId,
  onAnalisisComplete
}) => {
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tipoEstudio, setTipoEstudio] = useState('radiografia');
  const [contextoClinico, setContextoClinico] = useState('');
  const [analizando, setAnalizando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(true);

  const fileInputRef = useRef(null);

  // Estilos comunes para TextField
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      background: '#f8fafc',
      '&:hover': { background: '#f1f5f9' },
      '&.Mui-focused': { background: '#fff' }
    }
  };

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor seleccione un archivo de imagen válido');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        setError('La imagen no debe superar los 20MB');
        return;
      }
      setImagen(file);
      setPreview(URL.createObjectURL(file));
      setError(null);
      setResultado(null);
    }
  };

  const limpiarImagen = () => {
    setImagen(null);
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setResultado(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const limpiarTodo = () => {
    limpiarImagen();
    setContextoClinico('');
    setTipoEstudio('radiografia');
  };

  const analizarImagen = async () => {
    if (!imagen) return;

    setAnalizando(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('imagen', imagen);
      formData.append('tipo_estudio', tipoEstudio);
      if (contextoClinico) {
        formData.append('contexto_clinico', contextoClinico);
      }
      if (estudioId) {
        formData.append('estudio_id', estudioId);
      }

      const res = await fetch(`${API_URL}/emr/ia/analizar-imagen`, {
        method: 'POST',
        headers: {
          'X-User-Id': userId
        },
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Error al analizar la imagen');
      }

      const data = await res.json();
      setResultado(data);

      if (onAnalisisComplete) {
        onAnalisisComplete(data);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setAnalizando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          border: "none",
          overflow: "hidden",
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ImageIcon sx={{ fontSize: 26, color: "#fbbf24" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: 0.5 }}>
            Análisis de Imagen Médica
          </Typography>
          <Chip
            icon={<PsychologyIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
            label="GPT-4 Vision"
            size="small"
            sx={{
              ml: 1,
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 11
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {paciente && (
            <Chip
              label={paciente.nombre_completo}
              size="small"
              sx={{
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontWeight: 500,
                fontSize: 11
              }}
            />
          )}
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container>
          {/* Panel izquierdo: Imagen y opciones */}
          <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e2e8f0' } }}>
            {/* Sección: Cargar Imagen */}
            <SeccionFormulario icon={<UploadIcon />} titulo="Cargar Imagen" color="#f59e0b">
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: '#f8fafc',
                  minHeight: preview ? 'auto' : 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #e2e8f0',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: '#f59e0b',
                    background: '#fffbeb'
                  }
                }}
              >
                {!preview ? (
                  <>
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}>
                      <UploadIcon sx={{ fontSize: 32, color: '#d97706' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#1e3a5f', mb: 0.5 }}>
                      Seleccionar imagen médica
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
                      JPG, PNG, WEBP (máx. 20MB)
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }
                      }}
                    >
                      Subir Imagen
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImagenChange}
                      style={{ display: 'none' }}
                    />
                  </>
                ) : (
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Tooltip title="Eliminar imagen" arrow>
                      <IconButton
                        onClick={limpiarImagen}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#fee2e2',
                          color: '#dc2626',
                          '&:hover': { bgcolor: '#dc2626', color: '#fff' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 280,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1 }}>
                      {imagen?.name} ({(imagen?.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                  </Box>
                )}
              </Paper>
            </SeccionFormulario>

            {/* Sección: Tipo de Estudio */}
            {preview && (
              <SeccionFormulario icon={<BiotechIcon />} titulo="Tipo de Estudio" color="#6366f1">
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Estudio</InputLabel>
                  <Select
                    value={tipoEstudio}
                    label="Tipo de Estudio"
                    onChange={(e) => setTipoEstudio(e.target.value)}
                    sx={textFieldSx}
                  >
                    {TIPOS_ESTUDIO.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: '#e0e7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {React.cloneElement(tipo.icon, { sx: { fontSize: 16, color: '#4338ca' } })}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1e3a5f' }}>
                              {tipo.label}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                              {tipo.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </SeccionFormulario>
            )}

            {/* Sección: Contexto Clínico */}
            {preview && (
              <SeccionFormulario icon={<HospitalIcon />} titulo="Contexto Clínico (Opcional)" color="#10b981">
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Síntomas del paciente, sospecha diagnóstica, motivo del estudio..."
                  value={contextoClinico}
                  onChange={(e) => setContextoClinico(e.target.value)}
                  size="small"
                  sx={textFieldSx}
                />
              </SeccionFormulario>
            )}
          </Grid>

          {/* Panel derecho: Resultados */}
          <Grid item xs={12} md={6}>
            <SeccionFormulario icon={<PsychologyIcon />} titulo="Resultado del Análisis" color="#8b5cf6">
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {!resultado && !analizando ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 3,
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    <VisibilityIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
                    Sin análisis
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>
                    Suba una imagen y haga clic en "Analizar con IA"
                  </Typography>
                </Box>
              ) : analizando ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={60} sx={{ color: '#6366f1', mb: 2 }} />
                  <Typography sx={{ fontWeight: 600, color: '#1e3a5f', mb: 0.5 }}>
                    Analizando imagen...
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                    GPT-4 Vision está procesando la imagen
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Disclaimer */}
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: 12 } }}
                  >
                    {resultado.disclaimer}
                  </Alert>

                  {/* Análisis */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#6366f1', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                      Análisis Detallado
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {resultado.analisis}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Hallazgos */}
                  {resultado.hallazgos?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#10b981', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon sx={{ fontSize: 16 }} />
                        Hallazgos Detectados ({resultado.hallazgos.length})
                      </Typography>
                      <List dense sx={{ bgcolor: '#f0fdf4', borderRadius: 2, py: 0.5 }}>
                        {resultado.hallazgos.map((hallazgo, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckIcon sx={{ fontSize: 16, color: '#10b981' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={hallazgo}
                              primaryTypographyProps={{ fontSize: 13, color: '#166534' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Recomendaciones */}
                  {resultado.recomendaciones?.length > 0 && (
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#3b82f6', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon sx={{ fontSize: 16 }} />
                        Recomendaciones ({resultado.recomendaciones.length})
                      </Typography>
                      <List dense sx={{ bgcolor: '#eff6ff', borderRadius: 2, py: 0.5 }}>
                        {resultado.recomendaciones.map((rec, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <InfoIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={rec}
                              primaryTypographyProps={{ fontSize: 13, color: '#1e40af' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              )}
            </SeccionFormulario>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0", background: "#fff" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            borderColor: "#e2e8f0",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
          }}
        >
          Cerrar
        </Button>

        {resultado && (
          <Button
            onClick={limpiarTodo}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { background: "#f1f5f9", borderColor: "#94a3b8" }
            }}
          >
            Nuevo Análisis
          </Button>
        )}

        {preview && !resultado && (
          <Button
            variant="contained"
            startIcon={analizando ? <CircularProgress size={18} color="inherit" /> : <PsychologyIcon />}
            onClick={analizarImagen}
            disabled={analizando}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 6px 16px rgba(99, 102, 241, 0.4)"
              },
              "&:disabled": {
                background: "#e2e8f0",
                color: "#94a3b8"
              }
            }}
          >
            {analizando ? 'Analizando...' : 'Analizar con IA'}
          </Button>
        )}

        {resultado && (
          <Button
            variant="contained"
            onClick={() => {
              if (onAnalisisComplete) {
                onAnalisisComplete(resultado);
              }
              onClose();
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 4,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)"
              }
            }}
          >
            Guardar en Expediente
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnalizadorImagenes;
